import type { ReactNode } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { Inbox, ArrowRight, Sparkles } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface BlankSlateAction {
  label: string
  /** Router path to navigate to, e.g. '/intake'. */
  to?: string
  /** Custom click handler (takes precedence over `to`). */
  onClick?: () => void
  variant?: 'primary' | 'secondary'
}

interface BlankSlateProps {
  /** Lucide icon component; defaults to an inbox. */
  icon?: LucideIcon
  title: string
  /** Short explanation of what will appear here once data is ingested. */
  description: ReactNode
  actions?: BlankSlateAction[]
  /** Optional ordered onboarding steps shown beneath the description. */
  steps?: string[]
  /** Tight padding and a small icon — for 1×1 dashboard cells, not a full page. */
  compact?: boolean
  className?: string
}

/**
 * Empty / onboarding state shown when demo data is off and no real client data
 * has been ingested yet. Every module renders one of these so a fresh tenant
 * sees a guided blank slate rather than fabricated numbers.
 */
export default function BlankSlate({
  icon: Icon = Inbox,
  title,
  description,
  actions = [],
  steps,
  compact = false,
  className = '',
}: BlankSlateProps) {
  const navigate = useNavigate()

  return (
    <div
      className={clsx(
        'card flex flex-col items-center justify-center text-center',
        compact ? 'px-2 py-2' : 'px-8 py-14',
        className,
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-center rounded-2xl border border-amber-500/20 bg-amber-500/5',
          compact ? 'mb-1.5 h-7 w-7' : 'mb-4 h-14 w-14',
        )}
      >
        <Icon size={compact ? 14 : 26} className="text-amber-300/80" />
      </div>
      <h3 className={clsx('font-semibold text-stone-100', compact ? 'text-[12px]' : 'text-[15px]')}>
        {title}
      </h3>
      <p
        className={clsx(
          'leading-relaxed text-stone-400',
          compact ? 'mt-1 max-w-full text-[10px] leading-snug' : 'mt-2 max-w-md text-[12.5px]',
        )}
      >
        {description}
      </p>

      {steps && steps.length > 0 && (
        <ol className="mt-5 w-full max-w-sm space-y-2 text-left">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[12px] text-stone-300">
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-500/5 text-[10px] font-semibold text-amber-300">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      )}

      {actions.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
          {actions.map((action, i) => (
            <button
              key={i}
              type="button"
              onClick={() => (action.onClick ? action.onClick() : action.to && navigate(action.to))}
              className={`flex items-center gap-1.5 text-[12px] ${
                action.variant === 'secondary' ? 'btn-secondary' : 'btn-primary'
              }`}
            >
              {action.variant !== 'secondary' && <Sparkles size={12} />}
              {action.label}
              <ArrowRight size={12} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
